import { createTheme } from '@mui/material/styles';
import { MAP_UI } from './theme/mapViewUi';

// Unified typography settings (same across themes)
const typographySettings = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
    letterSpacing: '-0.02em',
  },
  h2: {
    fontWeight: 700,
    fontSize: '2rem',
    letterSpacing: '-0.01em',
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.75rem',
    letterSpacing: '-0.01em',
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
    letterSpacing: '0',
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
    letterSpacing: '0',
  },
  h6: {
    fontWeight: 600,
    fontSize: '1rem',
    letterSpacing: '0.01em',
  },
  subtitle1: {
    fontWeight: 500,
    fontSize: '1rem',
    letterSpacing: '0.01em',
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
  },
  body1: {
    fontSize: '1rem',
    letterSpacing: '0.01em',
  },
  body2: {
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
  },
  button: {
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'none',
  },
  caption: {
    fontSize: '0.75rem',
    letterSpacing: '0.03em',
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
};

const sharedPaletteColors = {
  primary: {
    main: MAP_UI.accent,
    dark: MAP_UI.accentDark,
    light: MAP_UI.accentSoft,
    contrastText: '#ffffff',
    lighter: MAP_UI.accentTint,
  },
  secondary: {
    main: '#118AB2',
  },
  success: {
    main: MAP_UI.deviceOnlineBorder,
    dark: MAP_UI.onlineChipText,
    light: MAP_UI.onlineChipBg,
    lighter: MAP_UI.onlineChipBg,
    contrastText: '#ffffff',
  },
  error: {
    main: MAP_UI.deviceOfflineBorder,
    dark: MAP_UI.offlineChipText,
    light: MAP_UI.offlineChipBg,
    lighter: MAP_UI.offlineChipBg,
    contrastText: '#ffffff',
  },
  warning: {
    main: MAP_UI.accentMuted,
    dark: MAP_UI.accentDark,
    light: MAP_UI.accentBgLight,
    lighter: MAP_UI.accentTint,
    contrastText: '#ffffff',
  },
  info: {
    main: '#118AB2',
    light: '#e3f2fd',
    lighter: '#e3f2fd',
  },
};

const sharedComponents = {
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderColor: 'rgba(0, 0, 0, 0.08)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      containedPrimary: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
        },
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        '&.Mui-selected': {
          color: MAP_UI.accentDark,
          backgroundColor: MAP_UI.accentTint,
          '&:hover': {
            backgroundColor: MAP_UI.accentTintHover,
          },
        },
      },
    },
  },
};

// Light theme (default)
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    ...sharedPaletteColors,
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#6C757D',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: MAP_UI.accentTint,
    },
  },
  typography: typographySettings,
  components: {
    ...sharedComponents,
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
});

// Dark theme (NOC Monitor style)
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    ...sharedPaletteColors,
    background: {
      default: '#0D1117',
      paper: '#161B22',
    },
    text: {
      primary: '#E6EDF3',
      secondary: '#8B949E',
    },
    divider: 'rgba(255, 255, 255, 0.1)',
    action: {
      hover: 'rgba(255, 255, 255, 0.05)',
      selected: 'rgba(96, 125, 139, 0.22)',
    },
  },
  typography: typographySettings,
  components: {
    ...sharedComponents,
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});
